import { X } from "lucide-react";
import React from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { JobExecution } from "../../../services/JobService";
import { formatNumber } from "../../../utils/numberUtils";

interface JobDetailsModalProps {
  execution: JobExecution | null;
  onClose: () => void;
}

const JobDetailsModal: React.FC<JobDetailsModalProps> = ({ execution, onClose }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  if (!execution) return null;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (durationMs: number | null) => {
    if (durationMs == null) return "N/A";
    if (durationMs < 1000) return `${durationMs}ms`;
    return `${formatNumber(durationMs / 1000)}s`;
  };

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="py-3 border-b border-gray-200 dark:border-rp-separator">
      <dt className={`text-sm font-medium ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>
        {label}
      </dt>
      <dd className={`mt-1 text-sm ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
        {value || "N/A"}
      </dd>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-rp-black dark:bg-opacity-75" />

        {/* Modal */}
        <div
          className={`relative inline-block w-full max-w-3xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform ${
            isDarkMode ? "bg-rp-surface" : "bg-white"
          } rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className={`text-lg font-medium leading-6 ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                Job Execution Details
              </h3>
              <p className={`mt-1 text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>
                Complete information for this job execution
              </p>
            </div>
            <button
              onClick={onClose}
              className={`rounded-md ${
                isDarkMode ? "text-rp-grey/50 hover:text-rp-grey/70" : "text-gray-400 hover:text-gray-500"
              } focus:outline-none`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="mt-4">
            <dl className="divide-y divide-gray-200 dark:divide-rp-separator">
              <DetailRow label="Job Name" value={execution.jobName} />
              <DetailRow label="Execution ID" value={execution.id} />
              <DetailRow label="Execution Name" value={execution.executionName} />
              
              <DetailRow
                label="Status"
                value={
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      execution.status === "SUCCEEDED"
                        ? isDarkMode
                          ? "bg-green-900/30 text-green-300"
                          : "bg-green-100 text-green-800"
                        : execution.status === "FAILED"
                        ? isDarkMode
                          ? "bg-red-900/30 text-red-300"
                          : "bg-red-100 text-red-800"
                        : execution.status === "RUNNING"
                        ? isDarkMode
                          ? "bg-blue-900/30 text-blue-300"
                          : "bg-blue-100 text-blue-800"
                        : isDarkMode
                        ? "bg-rp-surface-raised text-rp-grey/70"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {execution.status}
                  </span>
                }
              />

              <DetailRow label="Trigger Type" value={execution.triggerType} />
              <DetailRow label="Triggered By" value={execution.triggeredBy} />
              <DetailRow label="Created" value={formatDate(execution.createdDatetimeUtc)} />
              <DetailRow label="Updated" value={formatDate(execution.updatedDatetimeUtc)} />
              <DetailRow label="Started At" value={formatDate(execution.startedAt)} />
              <DetailRow label="Completed At" value={formatDate(execution.completedAt)} />
              <DetailRow label="Duration" value={formatDuration(execution.durationMs)} />
              <DetailRow label="Retry Count" value={execution.retryCount} />
              
              {execution.errorCode && (
                <DetailRow label="Error Code" value={execution.errorCode} />
              )}
              
              {execution.jobMetadata && Object.keys(execution.jobMetadata).length > 0 && (
                <DetailRow
                  label="Job Metadata"
                  value={
                    <pre className={`p-3 rounded-md overflow-x-auto text-xs ${isDarkMode ? "bg-rp-black" : "bg-gray-50"}`}>
                      {JSON.stringify(execution.jobMetadata, null, 2)}
                    </pre>
                  }
                />
              )}
              
              {execution.errorMessage && (
                <DetailRow
                  label="Error Message"
                  value={
                    <div className={`p-3 rounded-md ${isDarkMode ? "bg-red-900/20 text-red-300" : "bg-red-50 text-red-800"}`}>
                      {execution.errorMessage}
                    </div>
                  }
                />
              )}

            </dl>
          </div>

          {/* Footer */}
          <div className="mt-5 sm:mt-6">
            <button
              onClick={onClose}
              className={`w-full inline-flex justify-center rounded-md border px-4 py-2 text-base font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm ${
                isDarkMode
                  ? "border-rp-separator bg-rp-surface text-rp-grey hover:bg-rp-surface-raised focus:ring-rp-amber"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-blue-500"
              }`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsModal;
